import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { organizationSlugSchema } from '@gatecheckpro/contracts';
import { Button, Card, Field } from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useSession, useViewer } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';

function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function CreateOrganizationPage() {
  const navigate = useNavigate();
  const viewer = useViewer();
  const { reload } = useSession();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [contactEmail, setContactEmail] = useState(viewer.email);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const effectiveSlug = slugTouched ? slug : suggestSlug(name);
  const slugProblem =
    effectiveSlug.length === 0
      ? undefined
      : organizationSlugSchema.safeParse(effectiveSlug).success
        ? undefined
        : 'Use lowercase letters, numbers and hyphens, 3–48 characters.';

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const organization = await api.request<{ organizationId: string }>(
        '/api/v1/organizer/organizations',
        {
          method: 'POST',
          body: { name, slug: effectiveSlug, countryCode: 'NG', contactEmail },
          // A dropped response must not create a second organization. The key is
          // stable for this form instance, so a retry replays the first result.
          idempotencyKey: `create-organization:${viewer.userId}:${effectiveSlug}`,
        },
      );
      await reload();
      void navigate(`/o/${organization.organizationId}`, { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="gc-auth">
      <div className="gc-auth__card gc-stack" style={{ maxWidth: '32rem' }}>
        <Card>
          <form className="gc-stack" onSubmit={(event) => void submit(event)}>
            <div className="gc-stack gc-stack--tight">
              <span className="gc-eyebrow">New organization</span>
              <h1>Set up your workspace</h1>
              <p className="gc-muted">
                You become its owner. Verification comes later — you can set up an organization
                before CABS has reviewed it.
              </p>
            </div>

            <FailureNotice error={error} />

            <Field
              label="Organization name"
              name="name"
              required
              placeholder="Lagos Live Collective"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Field
              label="Workspace address"
              name="slug"
              required
              hint="Short, lowercase, and permanent. It identifies you across GateCheckPro."
              error={slugProblem}
              value={effectiveSlug}
              onChange={(event) => {
                setSlugTouched(true);
                setSlug(event.target.value);
              }}
            />
            <Field
              label="Contact email"
              type="email"
              name="contactEmail"
              required
              hint="Where CABS reaches you about this organization."
              value={contactEmail}
              onChange={(event) => setContactEmail(event.target.value)}
            />

            <Button
              type="submit"
              variant="primary"
              size="large"
              block
              busy={busy}
              disabled={slugProblem !== undefined || name.length < 2}
            >
              Create organization
            </Button>
          </form>
        </Card>

        <p className="gc-muted">
          <Link to="/organizations">Back to your organizations</Link>
        </p>
      </div>
    </main>
  );
}
